import { createState, css, Stateful, type Component } from "dreamland/core";
import { router } from "dreamland/router";
import { getAccountStatuses, getCurrentAccount, getAccount, getRelationship, follow, unfollow } from "../api/accounts";
import { Account, AccountStatusesParams, Status, auth } from "../api";
import { authStorage } from "../api/storage";
import StatusView from "../components/Status";
import { processContent } from "../utils/content";

const Profile: Component<{id?: string}, {}> = function (cx) {
    let state: Stateful<{
        account: Account|null;
        statuses: Status[];
        isFollowing: boolean;
        tab: "posts"|"replies"|"media";
    }> = createState({
        account: null,
        statuses: [],
        isFollowing: false,
        tab: "posts"
    });

    const loadStatuses = async (params: AccountStatusesParams = {}) => {
        if (!state.account?.id) return;
        let statuses: Status[] = [];
        if (params.max_id === undefined) {
            try {
            statuses.push(...await getAccountStatuses(state.account.id, {pinned: true}));
            } catch (e) {
                console.error("failed to load pinned statuses", e);
            }
        }
        statuses.push(...await getAccountStatuses(state.account.id, params));
        state.statuses = statuses;
        window.scrollTo(0, 0);
    }
    
    cx.mount = async () => {
        // Check auth before loading
        if (!auth.isAuthenticated()) {
            router.navigate("/login");
            return;
        }

        let accountId = this.id?.replace("/user/" , "");

        if (!accountId) {
            // Current user's profile
            state.account = await getCurrentAccount();
            accountId = state.account.id;
        } else {
            state.account = await getAccount(accountId);
            state.isFollowing = await getRelationship(accountId).then(rel => rel.following);
        }

        console.debug("loaded account", state.account);

        refresh();
    }

    const refresh = async () => {
        let params: AccountStatusesParams = {};
        switch (state.tab) {
            case "media":
                params.only_media = true;
                break;
            case "replies":
                params.exclude_replies = false;
                break;
            case "posts":
            default:
                params.exclude_replies = true;
                break;
        }
        console.debug("loading statuses with params", params);
        await loadStatuses(params);
    }

    use(state.tab).listen(async (newTab) => {
        console.debug("tab changed to", newTab);
        await refresh();
    });

    const loadNext = () => {
        if (state.statuses.length === 0) return;
        const lastStatus = state.statuses[state.statuses.length - 1];
        loadStatuses({ max_id: lastStatus.id });
    }

    const loadPrev = () => {
        if (state.statuses.length === 0) return;
        const firstStatus = state.statuses[0];
        loadStatuses({ min_id: firstStatus.id });
    }

	return (
		<div class="page">
			{use(state.account)
            .zip(use(state.isFollowing))
            .map(([account, isFollowing]) => account ? (
				<div>
                    <div class="profile-header-wrapper">
                        <div class="profile-header-container">
                            <img src={account.header} alt="Header" class="profile-header" />
                        </div>
                        <img src={account.avatar} alt="Avatar" class="profile-avatar" />
                    </div>
                    <div class="profile-info">
                    <div class="profile-info-top">
                        <div class="profile-name">
                            <h1 attr:innerHTML={processContent(account.display_name || account.username, account.emojis)}></h1>
                            <p>@{account.acct}</p>
                        </div>
                        <button on:click={async () => {
                            if (account.id === authStorage.userId) {
                                router.navigate("/settings");
                            } else {
                                let ship
                                if (isFollowing) {
                                    ship = await unfollow(account.id);
                                } else {
                                    ship = await follow(account.id);
                                }
                                isFollowing = ship.following;
                            }
                        }} class={
                            isFollowing || account.id == authStorage.userId ? "secondary" : "primary"
                        }>
                            {account.id === authStorage.userId ? (
                            <>
                                <span class="material-symbols-sharp">edit</span>
                                Edit Profile
                            </>
                        ) : (isFollowing ? (
                            <>
                                <span class="material-symbols-sharp">person_remove</span>
                                Unfollow
                            </>
                            ) : (
                                <>
                                <span class="material-symbols-sharp">person_add</span>
                                Follow
                            </>)
                                )}
                        </button>
                    </div>
					<p attr:innerHTML={processContent(account.note, account.emojis)}></p>
                    {account.fields.length > 0 ? (
                    <table class="profile-fields" border="1" cellspacing="0" cellpadding="6">
                        <tbody>
                        {account.fields.map(field => (
                            <tr class={`${field.verified_at !== null ? "verified" : ""}`} >
                                <td><strong>{field.name}:</strong></td>
                                <td>
                                    <span attr:innerHTML={processContent(field.value, account.emojis)}></span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    ) : null}
                    </div>
				</div>
			) : null)}
            <br />
            {use(state.tab).map(tab => (
                <div class="tabs profile-tabs">
                    <button class={tab === "posts" ? "primary" : "secondary"} on:click={async () => {
                        state.tab = "posts";
                    }}>
                        Posts
                    </button>
                    <button class={tab === "replies" ? "primary" : "secondary"} on:click={async () => {
                        state.tab = "replies";
                    }}>
                        Replies
                    </button>
                    <button class={tab === "media" ? "primary" : "secondary"} on:click={async () => {
                        state.tab = "media";
                    }}>
                        Media
                    </button>
            </div>
            ))}
            <br />
			{use(state.statuses).mapEach((status) => (
                <>
                    <StatusView status={status} />
                    <hr />
                </>
            ))}
            <div class="pagination">
                <a on:click={loadPrev}>&laquo; Prev</a>
                <a on:click={loadNext}>Next &raquo;</a>
            </div>
		</div>
	);
};

Profile.style = css`
    .profile-header-wrapper {
        position: relative;
        margin-bottom: 3rem;
    }

    .profile-header-container {
        aspect-ratio: 3 / 1;
        overflow: hidden;
    }

    .profile-header {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .profile-avatar {
        width: 7rem;
        height: 7rem;
        border: 4px solid white;
        position: absolute;
        left: 1rem;
        bottom: -2.5rem; /* inset a bit into the header */
        object-fit: cover;
        background: white;
    }

    .profile-info {
        /* padding: 0 1rem; */
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .profile-info-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .profile-fields {
        margin-top: 0.5rem;
        border-style: solid;
        border-collapse: collapse;
    }

    .profile-fields tr.verified {
        background-color: #eaeaea;
    }

    .profile-fields tr.verified > td:last-child > span:after {
        content: "check";
        font-family: "Material Symbols Sharp";
        margin-left: 0.25em;
        font-size: 1em;
        vertical-align: middle;
    }

    .profile-name p {
        color: #666;
    }
    

    .pagination {
        display: flex;
        justify-content: space-between;
        margin: 16px 0;
    }

    h1 {
        margin-bottom: 0;
        margin-top: 0;
    }

    .tabs {
        display: flex;
        width: 100%;
        justify-content: stretch;
    }

    .tabs button {
        flex: 1;
        justify-content: center;
        border: none;
        padding: 0.5em 1em;
        background: none;
        color: inherit;
        border-bottom: 3px solid transparent;
    }

    .tabs button:hover {
        border-bottom: 3px solid #ccc;
    }

    .tabs button.primary {
        border-bottom: 3px solid #000;
        font-weight: bold;
    }
`

export default Profile;